// D-SAAS-OBS-1 Phase 1 — Cost & Profitability (built to the approved mockup in docs/design/D-SAAS-OBS-1/).
// Waterfall per license dollar: infra% bucket (actual Azure measured against it) + S·M·L·E reserve% +
// net pool split Blue Dev / Owl. Fail-visible cost: when Azure cost is unavailable, rows show "—",
// never $0.00. Alert contact list (CPU emails) managed here too. SaaS-view only (routed RequireMode saas).
import { useEffect, useState } from 'react'
import { DollarSign, Users, Plus, Trash2 } from 'lucide-react'
import * as api from '../../api/client'

const MUTE = 'var(--color-text-mute, #6b7280)'
const card: React.CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e7e5e4)', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }
const th: React.CSSProperties = { textAlign: 'left', fontSize: 10, color: MUTE, textTransform: 'uppercase', letterSpacing: '.03em', padding: '7px 7px', borderBottom: '1px solid var(--color-border, #e7e5e4)' }
const thR: React.CSSProperties = { ...th, textAlign: 'right' }
const td: React.CSSProperties = { padding: '8px 7px', fontSize: 12.5, borderBottom: '1px solid #f1f0ee', fontVariantNumeric: 'tabular-nums' }
const tdR: React.CSSProperties = { ...td, textAlign: 'right' }
const kpi: React.CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e7e5e4)', borderRadius: 10, padding: '12px 13px' }
const badge = (bg: string, fg: string): React.CSSProperties => ({ display: 'inline-block', fontSize: 10.5, fontWeight: 800, padding: '2px 9px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' })
const money = (n: number | null | undefined) => (n === null || n === undefined) ? '—' : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function ProfitabilityTab() {
  const [data, setData] = useState<any>(null)
  const [contacts, setContacts] = useState<any[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [fees, setFees] = useState<Record<string, string>>({})
  const [newEmail, setNewEmail] = useState(''); const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    try {
      const [p, c] = await Promise.all([api.opsProfitability(), api.opsAlertContacts()])
      setData(p); setContacts(c.contacts || []); setErr(null)
    } catch (e: any) { setErr(e?.message || 'Failed to load') }
  }
  useEffect(() => { load() }, [])

  const saveFee = async (pharmacyId: string) => {
    const v = parseFloat((fees[pharmacyId] || '').replace(/[,$]/g, ''))
    if (isNaN(v) || v < 0) return
    setBusy(true)
    try { await api.opsSetLicense(pharmacyId, v); await load() } finally { setBusy(false) }
  }
  const addContact = async () => {
    if (!newEmail.includes('@')) return
    setBusy(true)
    try { await api.opsAddAlertContact(newEmail.trim(), newName.trim() || undefined); setNewEmail(''); setNewName(''); await load() } finally { setBusy(false) }
  }
  const removeContact = async (id: string) => { setBusy(true); try { await api.opsRemoveAlertContact(id); await load() } finally { setBusy(false) } }

  if (err) return <div style={{ padding: 20, color: '#991b1b', fontSize: 13 }}>{err}</div>
  if (!data) return <div style={{ padding: 20, fontSize: 13, color: MUTE }}>Loading profitability…</div>

  const s = data.settings || { reserve_pct: 25, infra_pct: 15, dev_split_pct: 25 }
  const port = data.portfolio
  const licensed = (data.customers || []).filter((c: any) => c.licensed)
  const owlPctLabel = (100 - s.reserve_pct - s.infra_pct) * (100 - s.dev_split_pct) / 100

  return (
    <div style={{ padding: '18px 22px', maxWidth: '100%' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 4px' }}>
        <DollarSign size={18} /> Cost &amp; Profitability
      </h2>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: MUTE }}>
        Allocation per license dollar: {s.infra_pct}% ops &amp; infra (bucket) · {s.reserve_pct}% sales/marketing/legal/emergency ·
        net pool split {s.dev_split_pct}% Blue Dev / {100 - s.dev_split_pct}% Owl (= {owlPctLabel.toFixed(0)}% of gross at nominal infra).
        Cost {data.cost_available
          ? <>as of <b>{data.cost_as_of ? new Date(data.cost_as_of).toLocaleString() : 'unknown'}</b>
              {data.cost_source === 'snapshot' ? ' (hourly snapshot)' : data.cost_source === 'live' ? ' (live Azure read)' : ''}</>
          : <b style={{ color: '#b45309' }}>unavailable — {data.cost_reason || 'Azure rate-limited'}</b>}.
      </p>

      {!data.cost_available && (
        <div style={{ background: '#fffbeb', border: '1px dashed #f59e0b', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, color: '#92400e', marginBottom: 12 }}>
          ⚠ Azure cost data unavailable right now. Profit cannot be computed — rows show “—”, never $0.00. Retry shortly.
        </div>
      )}

      {/* D-SAAS-OBS-1 Phase 2: figures come from the hourly snapshot, so a rate-limited Azure call no
          longer blanks this screen. When the snapshot itself ages past the threshold, say so plainly
          rather than presenting stale numbers as current. */}
      {data.cost_available && data.cost_stale && (
        <div style={{ background: '#fffbeb', border: '1px dashed #f59e0b', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, color: '#92400e', marginBottom: 12 }}>
          ⚠ Cost figures are older than {data.cost_stale_after_hours ?? 2}h (captured {data.cost_as_of ? new Date(data.cost_as_of).toLocaleString() : 'unknown'}).
          They are the last known-good numbers, not current — Azure Cost Management is likely throttling. Profit math below uses these values.
        </div>
      )}

      {(port || data.arr_usd != null) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 14 }}>
          <div style={kpi}><div style={{ fontSize: 10.5, color: MUTE, fontWeight: 700, textTransform: 'uppercase' }}>MRR</div><div style={{ fontSize: 21, fontWeight: 800 }}>{money(data.mrr_usd ?? port?.license_usd)}</div></div>
          <div style={kpi}><div style={{ fontSize: 10.5, color: MUTE, fontWeight: 700, textTransform: 'uppercase' }}>ARR</div><div style={{ fontSize: 21, fontWeight: 800, color: '#1d4ed8' }}>{money(data.arr_usd)}</div><div style={{ fontSize: 10.5, color: MUTE }}>MRR × 12, all licensed</div></div>
          {/* Cost/profit KPIs come only from the costed portfolio — never $0.00 when cost is down */}
          <div style={kpi}><div style={{ fontSize: 10.5, color: MUTE, fontWeight: 700, textTransform: 'uppercase' }}>Actual Azure (MTD)</div><div style={{ fontSize: 21, fontWeight: 800 }}>{port ? money(port.actual_azure_usd) : '—'}</div></div>
          <div style={kpi}><div style={{ fontSize: 10.5, color: MUTE, fontWeight: 700, textTransform: 'uppercase' }}>Blue Dev profit</div><div style={{ fontSize: 21, fontWeight: 800, color: '#6d28d9' }}>{port ? money(port.blue_dev_profit_usd) : '—'}</div></div>
          <div style={kpi}><div style={{ fontSize: 10.5, color: MUTE, fontWeight: 700, textTransform: 'uppercase' }}>Owl profit</div><div style={{ fontSize: 21, fontWeight: 800, color: '#0f766e' }}>{port ? <>{money(port.owl_profit_usd)} <span style={{ fontSize: 12 }}>({port.owl_margin_pct}%)</span></> : '—'}</div></div>
        </div>
      )}

      <div style={card}>
        <h3 style={{ fontSize: 14, margin: '0 0 2px' }}>Allocation &amp; profit by customer — month to date</h3>
        <p style={{ fontSize: 11.5, color: MUTE, margin: '0 0 10px' }}>
          Net pool = {100 - s.reserve_pct - s.infra_pct}% of license − infra overrun beyond the {s.infra_pct}% bucket. Blue Dev = {s.dev_split_pct}% of pool · Owl = {100 - s.dev_split_pct}%.
          Savings stay in the ops bucket; overruns hit both profits. Fee changes are effective-dated + audit-logged.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>Customer</th><th style={thR}>License / mo</th><th style={thR}>ARR</th><th style={thR}>S·M·L·E ({s.reserve_pct}%)</th>
            <th style={thR}>Infra bucket ({s.infra_pct}%)</th><th style={thR}>Actual Azure</th><th style={thR}>Δ bucket</th>
            <th style={thR}>Blue Dev</th><th style={thR}>Owl profit</th><th style={thR}>Owl %</th>
          </tr></thead>
          <tbody>
            {(data.customers || []).map((c: any) => (
              <tr key={c.pharmacy_id}>
                <td style={td}><b>{c.name}</b></td>
                <td style={tdR}>
                  <input value={fees[c.pharmacy_id] ?? (c.monthly_license_usd != null ? String(c.monthly_license_usd) : '')}
                    onChange={e => setFees({ ...fees, [c.pharmacy_id]: e.target.value })}
                    placeholder="—" style={{ width: 84, border: '1px solid var(--color-border,#e7e5e4)', borderRadius: 7, padding: '5px 7px', fontSize: 12.5, textAlign: 'right' }} />
                  <button onClick={() => saveFee(c.pharmacy_id)} disabled={busy}
                    style={{ marginLeft: 5, fontSize: 11, fontWeight: 700, color: '#0f766e', background: 'none', border: 'none', cursor: 'pointer' }}>Save</button>
                </td>
                {/* ARR is contract math — shown even when Azure cost is unavailable */}
                <td style={{ ...tdR, color: '#1d4ed8', fontWeight: 700 }}>{c.arr_usd != null ? money(c.arr_usd) : '—'}</td>
                {!c.licensed ? (
                  <><td style={tdR} colSpan={4}>{c.actual_azure_usd != null ? <>Azure {money(c.actual_azure_usd)}</> : '—'}</td>
                    <td style={tdR}>—</td><td style={tdR}>—</td>
                    <td style={tdR}><span style={badge('#f1f5f9', '#64748b')}>no license set</span></td></>
                ) : c.cost_unavailable ? (
                  <><td style={tdR} colSpan={4}>—</td><td style={tdR}>—</td><td style={tdR}>—</td>
                    <td style={tdR}><span style={badge('#f1f5f9', '#64748b')}>cost unavailable</span></td></>
                ) : (
                  <>
                    <td style={tdR}>{money(c.smle_usd)}</td>
                    <td style={tdR}>{money(c.infra_bucket_usd)}</td>
                    <td style={tdR}>{money(c.actual_azure_usd)}</td>
                    <td style={{ ...tdR, fontWeight: 700, color: c.bucket_delta_usd >= 0 ? '#15803d' : '#991b1b' }}>
                      {c.bucket_delta_usd >= 0 ? `+${money(c.bucket_delta_usd)} under` : `${money(c.bucket_delta_usd)} OVER`}
                    </td>
                    <td style={{ ...tdR, fontWeight: 800, color: '#6d28d9' }}>{money(c.blue_dev_profit_usd)}</td>
                    <td style={{ ...tdR, fontWeight: 800, color: '#0f766e' }}>{money(c.owl_profit_usd)}</td>
                    <td style={tdR}>
                      <span style={c.owl_margin_pct >= 40 ? badge('#dcfce7', '#166534') : c.owl_margin_pct >= 25 ? badge('#fef9c3', '#854d0e') : badge('#fee2e2', '#991b1b')}>
                        {c.owl_margin_pct}%{c.owl_margin_pct < 25 ? ' ✗ reprice' : ''}
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {port && (
              <tr>
                <td style={{ ...td, fontWeight: 800, borderTop: '2px solid var(--color-border,#e7e5e4)' }}>Portfolio (licensed × {licensed.length})</td>
                <td style={{ ...tdR, fontWeight: 800, borderTop: '2px solid var(--color-border,#e7e5e4)' }}>{money(port.license_usd)}</td>
                <td style={{ ...tdR, fontWeight: 800, color: '#1d4ed8', borderTop: '2px solid var(--color-border,#e7e5e4)' }}>{money(data.arr_usd)}</td>
                <td style={{ ...tdR, borderTop: '2px solid var(--color-border,#e7e5e4)' }} colSpan={2}></td>
                <td style={{ ...tdR, fontWeight: 800, borderTop: '2px solid var(--color-border,#e7e5e4)' }}>{money(port.actual_azure_usd)}</td>
                <td style={{ ...tdR, borderTop: '2px solid var(--color-border,#e7e5e4)' }}></td>
                <td style={{ ...tdR, fontWeight: 800, color: '#6d28d9', borderTop: '2px solid var(--color-border,#e7e5e4)' }}>{money(port.blue_dev_profit_usd)}</td>
                <td style={{ ...tdR, fontWeight: 800, color: '#0f766e', borderTop: '2px solid var(--color-border,#e7e5e4)' }}>{money(port.owl_profit_usd)}</td>
                <td style={{ ...tdR, borderTop: '2px solid var(--color-border,#e7e5e4)' }}><span style={port.owl_margin_pct >= 40 ? badge('#dcfce7', '#166534') : badge('#fef9c3', '#854d0e')}>{port.owl_margin_pct}%</span></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Monthly history — figures reset on the 1st; a closed month freezes at its final snapshot.
          Computed from cost_snapshots (last batch per month), so history accrues from Aug 2026
          forward and is bounded by 13-month snapshot retention. */}
      {Array.isArray(data.months) && data.months.length > 0 && (
        <div style={card}>
          <h3 style={{ fontSize: 14, margin: '0 0 2px' }}>Month over month</h3>
          <p style={{ fontSize: 11.5, color: MUTE, margin: '0 0 10px' }}>
            Resets on the 1st. Closed months are frozen at their final cost snapshot; the current month is running month-to-date.
            Revenue per month uses the license fee that was effective at that month's end.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', maxWidth: '100%' }}>
            <thead><tr>
              <th style={th}>Month</th><th style={th}>Status</th><th style={thR}>Revenue</th><th style={thR}>Azure cost</th>
              <th style={thR}>Blue Dev</th><th style={thR}>Owl profit</th><th style={thR}>Owl %</th>
            </tr></thead>
            <tbody>
              {data.months.map((m: any) => (
                <tr key={m.month}>
                  <td style={{ ...td, fontWeight: 700 }}>{m.month}</td>
                  <td style={td}>{m.closed
                    ? <span style={badge('#f1f5f9', '#475569')} title={`Frozen at ${m.cost_captured_at ? new Date(m.cost_captured_at).toLocaleString() : ''}`}>closed</span>
                    : <span style={badge('#dbeafe', '#1d4ed8')}>MTD</span>}</td>
                  <td style={tdR}>{money(m.license_usd)}</td>
                  <td style={tdR}>{money(m.azure_usd)}</td>
                  <td style={{ ...tdR, color: '#6d28d9', fontWeight: 700 }}>{money(m.blue_dev_profit_usd)}</td>
                  <td style={{ ...tdR, color: '#0f766e', fontWeight: 700 }}>{money(m.owl_profit_usd)}</td>
                  <td style={tdR}><span style={m.owl_margin_pct >= 40 ? badge('#dcfce7', '#166534') : m.owl_margin_pct >= 25 ? badge('#fef9c3', '#854d0e') : badge('#fee2e2', '#991b1b')}>{m.owl_margin_pct}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={card}>
        <h3 style={{ fontSize: 14, margin: '0 0 2px', display: 'flex', alignItems: 'center', gap: 7 }}><Users size={15} /> Alert contacts</h3>
        <p style={{ fontSize: 11.5, color: MUTE, margin: '0 0 10px' }}>
          These addresses receive resource alerts (e.g. tenant DB CPU &gt; threshold) when alerting ships in Phase 2. Add or deactivate anytime — changes are audit-logged.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', maxWidth: 640 }}>
          <thead><tr><th style={th}>Email</th><th style={th}>Name</th><th style={th}>Alerts</th><th style={th}>Status</th><th style={th}></th></tr></thead>
          <tbody>
            {contacts.map(c => (
              <tr key={c.id}>
                <td style={td}>{c.email}</td>
                <td style={td}>{c.display_name || '—'}</td>
                <td style={td}><span style={badge('#e0f2fe', '#0369a1')}>{c.alert_kinds}</span></td>
                <td style={td}>{c.active ? <span style={badge('#dcfce7', '#166534')}>active</span> : <span style={badge('#f1f5f9', '#64748b')}>inactive</span>}</td>
                <td style={td}>{c.active && <button onClick={() => removeContact(c.id)} disabled={busy} title="Deactivate"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b' }}><Trash2 size={14} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
          <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@example.com"
            style={{ border: '1px solid var(--color-border,#e7e5e4)', borderRadius: 7, padding: '6px 9px', fontSize: 12.5, width: 220 }} />
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name (optional)"
            style={{ border: '1px solid var(--color-border,#e7e5e4)', borderRadius: 7, padding: '6px 9px', fontSize: 12.5, width: 160 }} />
          <button onClick={addContact} disabled={busy || !newEmail.includes('@')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#0f766e', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <Plus size={13} /> Add contact
          </button>
        </div>
      </div>
    </div>
  )
}
